import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  fetchStatistic,
  type Statistic,
} from "@/features/dashboard/api";
import {
  listLatest,
  listLibraries,
  listMediaServers,
  listPlaying,
  type MediaServerClient,
  type MediaServerLibrary,
  type MediaServerPlayItem,
} from "@/features/library/api";
import { cn } from "@/shared/lib/cn";
import { proxyImageUrl } from "@/shared/lib/image";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

/** 媒体库：统计 + 绿联库列表 / 最新入库 / 在播 */
export function LibraryPage() {
  const [stat, setStat] = useState<Statistic | null>(null);
  const [clients, setClients] = useState<MediaServerClient[]>([]);
  const [server, setServer] = useState<string>("");
  const [libraries, setLibraries] = useState<MediaServerLibrary[]>([]);
  const [latest, setLatest] = useState<MediaServerPlayItem[]>([]);
  const [playing, setPlaying] = useState<MediaServerPlayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBase = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([
        fetchStatistic().catch(() => null),
        listMediaServers().catch(() => [] as MediaServerClient[]),
      ]);
      setStat(s);
      const list = Array.isArray(c) ? c : [];
      setClients(list);
      setServer((prev) => prev || list[0]?.name || "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (name: string) => {
    if (!name) {
      setLibraries([]);
      setLatest([]);
      setPlaying([]);
      return;
    }
    setDetailLoading(true);
    try {
      const [libs, lat, play] = await Promise.all([
        listLibraries(name).catch(() => []),
        listLatest(name, 16).catch(() => []),
        listPlaying(name, 8).catch(() => []),
      ]);
      setLibraries(Array.isArray(libs) ? libs : []);
      setLatest(Array.isArray(lat) ? lat : []);
      setPlaying(Array.isArray(play) ? play : []);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadBase();
  }, [loadBase]);

  useEffect(() => {
    void loadDetail(server);
  }, [server, loadDetail]);

  return (
    <div>
      <PageHeader
        title="媒体库"
        description="统计 · 库列表 · 最新入库 · 正在播放"
        actions={
          <Button
            variant="ghost"
            onClick={() => {
              void loadBase();
              void loadDetail(server);
            }}
            disabled={loading || detailLoading}
          >
            刷新
          </Button>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {stat ? (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Tile label="电影" value={stat.movie_count} />
          <Tile label="剧集" value={stat.tv_count} />
          <Tile label="集数" value={stat.episode_count} />
          <Tile label="本月电影" value={stat.movie_count_month} />
          <Tile label="本月剧集" value={stat.tv_count_month} />
          <Tile label="本月集" value={stat.episode_count_month} />
        </div>
      ) : null}

      {clients.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {clients.map((c) => (
            <button
              key={c.name}
              type="button"
              onClick={() => setServer(c.name)}
              className={cn(
                "h-8 rounded-[var(--radius-sm)] border px-3 text-[13px]",
                server === c.name
                  ? "border-brand/40 bg-brand-muted text-brand"
                  : "border-border text-fg-muted hover:bg-bg-hover",
              )}
            >
              {c.name}
              {c.type ? (
                <span className="ml-1 text-[11px] opacity-70">({c.type})</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : !loading ? (
        <EmptyState
          title="未配置媒体服务器"
          description="请在后端配置绿联等媒体服务器"
        />
      ) : null}

      {detailLoading ? (
        <div className="mb-3 flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载库内容…
        </div>
      ) : null}

      {server && !detailLoading ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Section title="媒体库">
            {libraries.length === 0 ? (
              <EmptyState title="无媒体库" />
            ) : (
              <ul className="flex flex-col gap-2">
                {libraries.map((lib, i) => (
                  <li
                    key={`${lib.id ?? lib.name}-${i}`}
                    className="flex items-center gap-3 rounded-[var(--radius-md)] border border-border bg-bg-card px-3 py-2"
                  >
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-bg-hover">
                      {proxyImageUrl(lib.image) ? (
                        <img
                          src={proxyImageUrl(lib.image)}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-medium">
                        {lib.name || "未命名"}
                      </div>
                      <div className="truncate text-[11px] text-fg-subtle">
                        {[lib.type, formatPath(lib.path)].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    {lib.link ? (
                      <a
                        href={lib.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-brand hover:underline"
                      >
                        打开
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <div className="flex flex-col gap-5">
            <Section title="最新入库">
              <PlayList items={latest} empty="暂无最新入库" />
            </Section>
            <Section title="正在播放">
              <PlayList items={playing} empty="当前无人播放" />
            </Section>
          </div>
        </div>
      ) : null}

      <p className="mt-6 text-[12px] text-fg-subtle">
        单片是否在库可在{" "}
        <Link className="text-brand hover:underline" to="/search">
          搜索
        </Link>{" "}
        → 详情页查看。
      </p>
    </div>
  );
}

function formatPath(path?: string | string[] | null): string {
  if (!path) return "";
  if (Array.isArray(path)) return path.join(", ");
  return path;
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-2 text-[13px] font-medium text-fg-muted">{title}</h2>
      {children}
    </section>
  );
}

function PlayList({
  items,
  empty,
}: {
  items: MediaServerPlayItem[];
  empty: string;
}) {
  if (!items.length) return <EmptyState title={empty} />;
  return (
    <ul className="flex flex-col gap-2">
      {items.map((it, i) => (
        <li
          key={`${it.id ?? it.title}-${i}`}
          className="flex items-center gap-3 rounded-[var(--radius-md)] border border-border bg-bg-card px-3 py-2"
        >
          <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-bg-hover">
            {proxyImageUrl(it.image) ? (
              <img
                src={proxyImageUrl(it.image)}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : null}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium">
              {it.title || "未命名"}
            </div>
            <div className="truncate text-[11px] text-fg-subtle">
              {[it.subtitle, it.type].filter(Boolean).join(" · ")}
            </div>
            {it.percent != null && it.percent > 0 ? (
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-bg-hover">
                <div
                  className="h-full bg-brand"
                  style={{ width: `${Math.min(it.percent, 100)}%` }}
                />
              </div>
            ) : null}
          </div>
          {it.link ? (
            <a
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] text-brand hover:underline"
            >
              播放
            </a>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function Tile({
  label,
  value,
}: {
  label: string;
  value?: number | null;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border border-border bg-bg-card p-4">
      <div className="text-[12px] text-fg-subtle">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-brand">
        {value == null ? "—" : value}
      </div>
    </div>
  );
}
